import React from "react";
import { Button } from "@/components/ui/button.tsx";
import {
  Scissors, Zap, Download, Package, Globe, Shield,
  ArrowRight, ChevronRight, BarChart3, Layers,
} from "lucide-react";
import { t, type Lang } from "./i18n.ts";

import { Particles } from "@/components/ui/particles.tsx";
import { BlurFade } from "@/components/ui/blur-fade.tsx";
import { ShimmerButton } from "@/components/ui/shimmer-button.tsx";
import { NumberTicker } from "@/components/ui/number-ticker.tsx";
import { WordRotate } from "@/components/ui/word-rotate.tsx";
import { AnimatedShinyText } from "@/components/ui/animated-shiny-text.tsx";
import { AnimatedGradientText } from "@/components/ui/animated-gradient-text.tsx";
import { MagicCard } from "@/components/ui/magic-card.tsx";
import { BorderBeam } from "@/components/ui/border-beam.tsx";
import { RetroGrid } from "@/components/ui/retro-grid.tsx";

interface Props {
  lang: Lang;
  setLang: (l: Lang) => void;
  onStart: () => void;
}

export function Landing({ lang, setLang, onStart }: Props) {
  const pl = lang === "pl";

  const materialWords = pl
    ? ["Drewno", "Szkło", "Blacha", "Kamień"]
    : ["Wood", "Glass", "Metal", "Stone"];

  return (
    <div className="landing-dark min-h-screen bg-[#0a0a0f] text-gray-100" style={{ "--magic-card-bg": "#0d0d14" } as React.CSSProperties}>
      {/* Nav */}
      <nav className="border-b border-white/10 bg-[#0a0a0f]/80 backdrop-blur sticky top-0 z-40">
        <div className="mx-auto max-w-6xl px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Scissors className="h-6 w-6" />
            <span className="text-xl font-bold">HolySheet</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex gap-1">
              <Button variant={pl ? "default" : "outline"} size="sm" className={pl ? "" : "border-white/20 text-gray-300 hover:bg-white/10"} onClick={() => setLang("pl")}>PL</Button>
              <Button variant={!pl ? "default" : "outline"} size="sm" className={!pl ? "" : "border-white/20 text-gray-300 hover:bg-white/10"} onClick={() => setLang("en")}>EN</Button>
            </div>
            <Button onClick={onStart}>
              {pl ? "Otwórz aplikację" : "Open app"}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative overflow-hidden min-h-[90vh] flex items-center">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url(/img/hero-bg.jpg)" }}
        />
        <div className="absolute inset-0 bg-[#0a0a0f]/75" />
        <Particles
          className="absolute inset-0"
          quantity={60}
          color="#60a5fa"
          ease={80}
          staticity={40}
          size={0.4}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-transparent to-[#0a0a0f]" />

        <div className="relative mx-auto max-w-6xl px-4 py-20 text-center">
          <BlurFade delay={0.1} inView>
            <div className="inline-flex items-center rounded-full border border-white/15 bg-white/5 px-4 py-1.5 mb-8">
              <AnimatedShinyText className="text-gray-400" shimmerWidth={120}>
                <Scissors className="inline h-4 w-4 mr-2" />
                {pl ? "Darmowy optymalizator rozkroju online" : "Free online cutting optimizer"}
              </AnimatedShinyText>
            </div>
          </BlurFade>

          <BlurFade delay={0.2} inView>
            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-4 text-white">
              {pl ? (
                <>
                  <span className="block">Mniej odpadu.</span>
                  <AnimatedGradientText
                    className="text-5xl md:text-7xl lg:text-8xl font-bold"
                    colorFrom="#3b82f6"
                    colorTo="#10b981"
                  >
                    Więcej zysku.
                  </AnimatedGradientText>
                </>
              ) : (
                <>
                  <span className="block">Less waste.</span>
                  <AnimatedGradientText
                    className="text-5xl md:text-7xl lg:text-8xl font-bold"
                    colorFrom="#3b82f6"
                    colorTo="#10b981"
                  >
                    More profit.
                  </AnimatedGradientText>
                </>
              )}
            </h1>
          </BlurFade>

          <BlurFade delay={0.3} inView>
            <div className="flex items-center justify-center gap-2 mb-6 text-2xl md:text-3xl text-gray-400">
              <span>{pl ? "Optymalizuj rozkrój:" : "Optimize cutting:"}</span>
              <WordRotate
                words={materialWords}
                className="text-2xl md:text-3xl font-semibold text-white"
                duration={2000}
              />
            </div>
          </BlurFade>

          <BlurFade delay={0.4} inView>
            <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              {pl
                ? "Algorytm WASM działa błyskawicznie w przeglądarce — zero czekania, zero opłat za serwer."
                : "WASM-powered algorithm runs instantly in your browser — zero waiting, zero server costs."}
            </p>
          </BlurFade>

          <BlurFade delay={0.5} inView>
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
              <ShimmerButton
                className="text-lg px-10 py-4 font-semibold"
                shimmerColor="#3b82f6"
                background="linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%)"
                onClick={onStart}
              >
                <Scissors className="h-5 w-5 mr-2" />
                {pl ? "Zacznij optymalizować" : "Start optimizing"}
                <ArrowRight className="h-5 w-5 ml-2" />
              </ShimmerButton>
              <button
                className="inline-flex items-center justify-center text-lg px-8 py-4 rounded-lg border border-white/15 text-gray-300 bg-white/5 backdrop-blur-sm hover:bg-white/10 hover:border-white/25 hover:text-white transition-all duration-300 cursor-pointer"
                onClick={() => {
                  document.getElementById("features")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                {pl ? "Zobacz jak to działa" : "See how it works"}
                <ChevronRight className="h-5 w-5 ml-1" />
              </button>
            </div>
          </BlurFade>

          {/* Quick stats */}
          <BlurFade delay={0.6} inView>
            <div className="flex flex-wrap justify-center gap-12 text-center">
              <div>
                <div className="text-4xl font-bold">&lt;<NumberTicker value={1} />s</div>
                <div className="text-sm text-gray-500">{pl ? "czas optymalizacji" : "optimization time"}</div>
              </div>
              <div>
                <div className="text-4xl font-bold"><NumberTicker value={4} /></div>
                <div className="text-sm text-gray-500">{pl ? "typy materiałów" : "material types"}</div>
              </div>
              <div>
                <div className="text-4xl font-bold"><NumberTicker value={100} />%</div>
                <div className="text-sm text-gray-500">{pl ? "w przeglądarce" : "in-browser"}</div>
              </div>
              <div>
                <div className="text-4xl font-bold">0 zł</div>
                <div className="text-sm text-gray-500">{pl ? "na start" : "to start"}</div>
              </div>
            </div>
          </BlurFade>
        </div>
      </section>

      {/* Materials strip */}
      <section className="border-y border-white/10 bg-[#0d0d14]">
        <div className="mx-auto max-w-6xl px-4 py-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: "🪵", name: pl ? "Drewno / Płyty" : "Wood / Panels", desc: pl ? "MDF, sklejka, płyta wiórowa" : "MDF, plywood, chipboard" },
              { icon: "🪟", name: pl ? "Szkło" : "Glass", desc: pl ? "hartowane, float, laminowane" : "tempered, float, laminated" },
              { icon: "⚙️", name: pl ? "Blacha" : "Sheet metal", desc: pl ? "stal, aluminium, miedź" : "steel, aluminum, copper" },
              { icon: "🪨", name: pl ? "Kamień" : "Stone", desc: pl ? "granit, kwarc, marmur" : "granite, quartz, marble" },
            ].map((m, i) => (
              <BlurFade key={m.name} delay={0.1 * i} inView>
                <div className="rounded-xl overflow-hidden cursor-pointer" onClick={onStart}>
                  <MagicCard
                    gradientFrom="#3b82f6"
                    gradientTo="#10b981"
                    gradientColor="#1e3a5f"
                    gradientOpacity={0.15}
                  >
                    <div className="text-center py-6 px-4">
                      <div className="text-4xl mb-2">{m.icon}</div>
                      <div className="font-semibold">{m.name}</div>
                      <div className="text-sm text-gray-500">{m.desc}</div>
                    </div>
                  </MagicCard>
                </div>
              </BlurFade>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-20 bg-[#0a0a0f]">
        <div className="mx-auto max-w-6xl px-4">
          <BlurFade delay={0.1} inView>
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
              {pl ? "Dlaczego " : "Why "}
              <AnimatedGradientText
                className="text-3xl md:text-4xl font-bold"
                colorFrom="#3b82f6"
                colorTo="#10b981"
              >
                HolySheet
              </AnimatedGradientText>
              ?
            </h2>
          </BlurFade>
          <BlurFade delay={0.2} inView>
            <p className="text-center text-gray-400 text-lg mb-14 max-w-2xl mx-auto">
              {pl
                ? "Profesjonalny optymalizator rozkroju, który działa od razu — bez instalacji, bez rejestracji."
                : "Professional cutting optimizer that works instantly — no installation, no registration."}
            </p>
          </BlurFade>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: <Zap className="h-8 w-8 text-yellow-500" />,
                title: pl ? "Błyskawiczny" : "Lightning fast",
                desc: pl
                  ? "Algorytm WASM wykonuje optymalizację w milisekundach. Brak opóźnień serwera — wszystko działa lokalnie."
                  : "WASM algorithm optimizes in milliseconds. No server delays — everything runs locally in your browser.",
                gradient: { from: "#eab308", to: "#f59e0b" },
              },
              {
                icon: <BarChart3 className="h-8 w-8 text-green-500" />,
                title: pl ? "Minimalizacja odpadu" : "Minimize waste",
                desc: pl
                  ? "Inteligentny algorytm testuje wiele strategii i wybiera optymalny rozkład. Wizualizacja resztek do ponownego użycia."
                  : "Smart algorithm tests multiple strategies and picks the optimal layout. Remnant visualization for reuse.",
                gradient: { from: "#22c55e", to: "#10b981" },
              },
              {
                icon: <Download className="h-8 w-8 text-blue-500" />,
                title: pl ? "Eksport PDF" : "PDF export",
                desc: pl
                  ? "Generuj profesjonalne plany rozkroju w PDF z wymiarami i oznaczeniami. Wydrukuj i zabierz do warsztatu."
                  : "Generate professional cut plans as PDF with dimensions and labels. Print and take to the workshop.",
                gradient: { from: "#3b82f6", to: "#6366f1" },
              },
              {
                icon: <Layers className="h-8 w-8 text-purple-500" />,
                title: pl ? "Wiele materiałów" : "Multiple materials",
                desc: pl
                  ? "Presety dla drewna, szkła, blachy i kamienia z właściwymi szerokościami rzazu i rozmiarami arkuszy."
                  : "Presets for wood, glass, metal and stone with correct kerf widths and stock sizes.",
                gradient: { from: "#a855f7", to: "#8b5cf6" },
              },
              {
                icon: <Package className="h-8 w-8 text-orange-500" />,
                title: pl ? "Magazyn materiałów" : "Stock inventory",
                desc: pl
                  ? "Zapisuj resztki do magazynu i wykorzystuj je w przyszłych projektach. Nigdy nie marnuj materiału."
                  : "Save remnants to inventory and reuse in future projects. Never waste material.",
                gradient: { from: "#f97316", to: "#ef4444" },
              },
              {
                icon: <Shield className="h-8 w-8 text-cyan-500" />,
                title: pl ? "Prywatność" : "Privacy first",
                desc: pl
                  ? "Twoje dane nie opuszczają przeglądarki. Optymalizacja działa offline. Konto tylko do zapisu projektów."
                  : "Your data never leaves your browser. Optimization works offline. Account only for saving projects.",
                gradient: { from: "#06b6d4", to: "#0ea5e9" },
              },
            ].map((f, i) => (
              <BlurFade key={f.title} delay={0.1 * i} inView>
                <div className="rounded-xl overflow-hidden h-full">
                  <MagicCard
                    gradientFrom={f.gradient.from}
                    gradientTo={f.gradient.to}
                    gradientColor={f.gradient.from}
                    gradientOpacity={0.1}
                  >
                    <div className="p-6">
                      <div className="mb-4 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-white/5">
                        {f.icon}
                      </div>
                      <h3 className="text-lg font-semibold mb-2">{f.title}</h3>
                      <p className="text-gray-400 leading-relaxed">{f.desc}</p>
                    </div>
                  </MagicCard>
                </div>
              </BlurFade>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-[#0d0d14] border-y border-white/10">
        <div className="mx-auto max-w-6xl px-4">
          <BlurFade delay={0.1} inView>
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-14">
              {pl ? "Jak to działa?" : "How it works?"}
            </h2>
          </BlurFade>

          <div className="grid md:grid-cols-3 gap-12">
            {[
              {
                step: "1",
                title: pl ? "Dodaj materiały i elementy" : "Add materials and pieces",
                desc: pl
                  ? "Wybierz typ materiału, podaj wymiary arkuszy i listę elementów do wycięcia."
                  : "Select material type, enter stock sheet dimensions and list of pieces to cut.",
              },
              {
                step: "2",
                title: pl ? "Kliknij Optymalizuj" : "Click Optimize",
                desc: pl
                  ? "Algorytm w ułamku sekundy przetestuje setki wariantów i wybierze optymalny rozkład."
                  : "The algorithm tests hundreds of variants in a fraction of a second and picks the optimal layout.",
              },
              {
                step: "3",
                title: pl ? "Eksportuj i tnij" : "Export and cut",
                desc: pl
                  ? "Pobierz plan w PDF, wyświetl w trybie pełnoekranowym na tablecie lub wydrukuj."
                  : "Download the plan as PDF, display fullscreen on a tablet, or print it out.",
              },
            ].map((s, i) => (
              <BlurFade key={s.step} delay={0.15 * i} inView>
                <div className="text-center">
                  <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary text-primary-foreground text-3xl font-bold mb-6">
                    {s.step}
                    <BorderBeam
                      size={60}
                      duration={4}
                      colorFrom="#3b82f6"
                      colorTo="#10b981"
                      borderWidth={2}
                    />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{s.title}</h3>
                  <p className="text-gray-400 leading-relaxed">{s.desc}</p>
                </div>
              </BlurFade>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24 overflow-hidden bg-[#0a0a0f]">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url(/img/cta-bg.jpg)" }}
        />
        <div className="absolute inset-0 bg-[#0a0a0f]/60" />
        <RetroGrid
          className="absolute inset-0"
          angle={65}
          cellSize={50}
          opacity={0.15}
          lightLineColor="#1e3a5f"
          darkLineColor="#1e3a5f"
        />
        <div className="relative mx-auto max-w-3xl px-4 text-center">
          <BlurFade delay={0.1} inView>
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              {pl ? "Gotowy na " : "Ready for "}
              <AnimatedGradientText
                className="text-3xl md:text-5xl font-bold"
                colorFrom="#3b82f6"
                colorTo="#10b981"
              >
                {pl ? "mniej odpadu" : "less waste"}
              </AnimatedGradientText>
              ?
            </h2>
          </BlurFade>
          <BlurFade delay={0.2} inView>
            <p className="text-lg text-gray-400 mb-10">
              {pl
                ? "Zacznij za darmo. Bez rejestracji, bez karty kredytowej. Po prostu otwórz i optymalizuj."
                : "Start for free. No registration, no credit card. Just open and optimize."}
            </p>
          </BlurFade>
          <BlurFade delay={0.3} inView>
            <ShimmerButton
              className="text-lg px-12 py-5 font-semibold mx-auto"
              shimmerColor="#10b981"
              background="linear-gradient(135deg, #064e3b 0%, #0f172a 100%)"
              onClick={onStart}
            >
              <Scissors className="h-5 w-5 mr-2" />
              {pl ? "Otwórz HolySheet" : "Open HolySheet"}
              <ArrowRight className="h-5 w-5 ml-2" />
            </ShimmerButton>
          </BlurFade>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 bg-[#0a0a0f]">
        <div className="mx-auto max-w-6xl px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-white">
            <Scissors className="h-4 w-4" />
            <span className="font-semibold">HolySheet</span>
            <span className="text-gray-500 text-sm">&copy; 2026</span>
          </div>
          <p className="text-sm text-gray-500">
            {pl ? "Optymalizuj rozkrój, oszczędzaj materiał" : "Optimize cuts, save material"}
          </p>
          <div className="flex items-center gap-1 text-sm text-gray-500">
            <Globe className="h-3 w-3" />
            holysheet.tech
          </div>
        </div>
      </footer>
    </div>
  );
}
